import type { ToleranceType } from '@frontend/api';
import { TOLERANCE_TYPES } from '@frontend/api';
import {
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
} from '@frontend/legacy-components';

import { NumberValueField } from './NumberInputValue';
import { TOLERANCE_TYPE_OPTIONS } from './constants';
import { getFieldError, getFieldTouched } from './formUtils';

type ToleranceInputGroupProps = {
  // Control props
  hasToleranceFieldName: string;
  hasTolerance: boolean;
  onHasToleranceChange: (checked: boolean) => void;

  // Tolerance type props
  toleranceTypeFieldName: string;
  toleranceType: ToleranceType;
  onToleranceTypeChange: (type: ToleranceType) => void;

  // Value props for different tolerance types
  symmetricFieldName: string;
  symmetricValue?: string;
  onSymmetricChange: (value: string) => void;

  upperFieldName: string;
  upperValue?: string;
  onUpperChange: (value: string) => void;

  lowerFieldName: string;
  lowerValue?: string;
  onLowerChange: (value: string) => void;

  // Error handling
  errors?: any;
  touched?: any;

  // Customization
  checkboxLabel?: string;
  toleranceLabel?: string;
  symmetricLabel?: string;
  upperLabel?: string;
  lowerLabel?: string;

  // NumberValueField props
  min?: number;
  max?: number;
};

export const ToleranceInputGroup = ({
  hasToleranceFieldName,
  hasTolerance,
  onHasToleranceChange,
  toleranceTypeFieldName,
  toleranceType,
  onToleranceTypeChange,
  symmetricFieldName,
  symmetricValue,
  onSymmetricChange,
  upperFieldName,
  upperValue,
  onUpperChange,
  lowerFieldName,
  lowerValue,
  onLowerChange,
  errors,
  touched,
  checkboxLabel = 'Include tolerance',
  toleranceLabel = 'Tolerance Type',
  symmetricLabel = 'Tolerance (±)',
  upperLabel = 'Upper Tolerance',
  lowerLabel = 'Lower Tolerance',
  min = 0,
  max,
}: ToleranceInputGroupProps) => {
  return (
    <>
      {/* Tolerance Checkbox */}
      <Checkbox
        name={hasToleranceFieldName}
        isChecked={hasTolerance}
        onChange={(e) => onHasToleranceChange(e.target.checked)}
        size="sm"
      >
        {checkboxLabel}
      </Checkbox>

      {/* Tolerance Inputs */}
      {hasTolerance && (
        <>
          {/* Tolerance Type Select */}
          <FormControl>
            <FormLabel>{toleranceLabel}</FormLabel>
            <Select
              name={toleranceTypeFieldName}
              options={TOLERANCE_TYPE_OPTIONS}
              defaultValue={TOLERANCE_TYPE_OPTIONS[0]}
              value={TOLERANCE_TYPE_OPTIONS.find(
                (option) => option.value === toleranceType,
              )}
              onChange={(
                selectedOption: {
                  value: ToleranceType;
                  label: string;
                } | null,
              ) => {
                if (selectedOption?.value) {
                  onToleranceTypeChange(selectedOption.value);
                }
              }}
              size="sm"
              isClearable={false}
            />
            {getFieldError(toleranceTypeFieldName, errors) &&
              getFieldTouched(toleranceTypeFieldName, touched) && (
                <FormErrorMessage>
                  {getFieldError(toleranceTypeFieldName, errors)}
                </FormErrorMessage>
              )}
          </FormControl>

          {/* Tolerance Value Inputs based on type */}
          {toleranceType === TOLERANCE_TYPES.SYMMETRIC ? (
            <FormControl>
              <FormLabel>{symmetricLabel}</FormLabel>
              <NumberValueField
                name={symmetricFieldName}
                value={symmetricValue || ''}
                onChange={onSymmetricChange}
                size="sm"
                min={min}
                max={max}
              />
              {getFieldError(symmetricFieldName, errors) &&
                getFieldTouched(symmetricFieldName, touched) && (
                  <FormErrorMessage>
                    {getFieldError(symmetricFieldName, errors)}
                  </FormErrorMessage>
                )}
            </FormControl>
          ) : (
            <>
              <FormControl>
                <FormLabel>{upperLabel}</FormLabel>
                <NumberValueField
                  name={upperFieldName}
                  value={upperValue || ''}
                  onChange={onUpperChange}
                  size="sm"
                  min={min}
                  max={max}
                />
                {getFieldError(upperFieldName, errors) &&
                  getFieldTouched(upperFieldName, touched) && (
                    <FormErrorMessage>
                      {getFieldError(upperFieldName, errors)}
                    </FormErrorMessage>
                  )}
              </FormControl>
              <FormControl>
                <FormLabel>{lowerLabel}</FormLabel>
                <NumberValueField
                  name={lowerFieldName}
                  value={lowerValue || ''}
                  onChange={onLowerChange}
                  size="sm"
                  min={min}
                  max={max}
                />
                {getFieldError(lowerFieldName, errors) &&
                  getFieldTouched(lowerFieldName, touched) && (
                    <FormErrorMessage>
                      {getFieldError(lowerFieldName, errors)}
                    </FormErrorMessage>
                  )}
              </FormControl>
            </>
          )}
        </>
      )}
    </>
  );
};
